<template>
  <a-modal
    v-model:open="modalVisible"
    :title="modalTitle"
    :footer="null"
    :destroyOnClose="true"
    centered
    width="420px"
    @cancel="handleClose"
  >
    <BindPhoneForm
      v-if="bindType === 'phone'"
      :phone="phoneBinder.form.phone"
      :sms-code="phoneBinder.form.code"
      :captcha-code="phoneBinder.captcha.code"
      :captcha-image="phoneBinder.captcha.image"
      :show-captcha="phoneBinder.showCaptcha.value"
      :can-send-code="phoneBinder.canSendCode.value"
      :sending-code="phoneBinder.sendingCode.value"
      :submitting="phoneBinder.submitting.value"
      :sms-button-text="smsButtonText"
      @update:phone="phoneBinder.form.phone = $event"
      @update:sms-code="phoneBinder.form.code = $event"
      @update:captcha-code="phoneBinder.captcha.code = $event"
      @send-sms="handleSendSms"
      @refresh-captcha="handleRefreshCaptcha"
      @submit="handleBindPhone"
    />

    <BindWechatQrcode
      v-else
      :qrcode-url="wechatBinder.qrcodeUrl.value"
      :loading="wechatBinder.loading.value"
      :expired="wechatBinder.expired.value"
      @refresh="handleRefreshWechatQrcode"
    />
  </a-modal>
</template>

<script setup lang="ts">
/**
 * 账号绑定弹窗
 */
import { computed, ref, watch } from 'vue'
import { message } from 'ant-design-vue'
import BindPhoneForm from './BindPhoneForm.vue'
import BindWechatQrcode from './BindWechatQrcode.vue'
import { useBindAccountModal, closeBindAccountModal } from './useBindAccountModal'
import { useBindAccountState } from './useBindAccountState'
import { useBindPhone } from './useBindPhone'
import { useBindWechat } from './useBindWechat'

const { visible, bindType } = useBindAccountModal()
const { userInfo, refreshBindUserInfo } = useBindAccountState()
const phoneBinder = useBindPhone()
const wechatBinder = useBindWechat()

const modalVisible = ref(false)

const modalTitle = computed(() => bindType.value === 'phone' ? '绑定手机号' : '绑定微信')

const smsButtonText = computed(() => {
  if (phoneBinder.countdown.value > 0) {
    return `${phoneBinder.countdown.value}s`
  }
  return phoneBinder.showCaptcha.value ? '发送验证码' : '获取验证码'
})

function resetState() {
  phoneBinder.reset()
  wechatBinder.reset()
}

function handleClose() {
  closeBindAccountModal()
  modalVisible.value = false
  resetState()
}

async function handleBindSuccess(successText: string) {
  message.success(successText)
  await refreshBindUserInfo()
  handleClose()
}

async function handleSendSms() {
  try {
    const result = await phoneBinder.sendSms()
    if (!result.requireCaptcha) {
      message.success('验证码已发送')
    }
  } catch (error) {
    message.error(error instanceof Error ? error.message : '发送验证码失败')
  }
}

async function handleRefreshCaptcha() {
  try {
    await phoneBinder.loadCaptcha()
  } catch (error) {
    message.error(error instanceof Error ? error.message : '获取图形验证码失败')
  }
}

async function handleBindPhone() {
  try {
    await phoneBinder.submit()
    if (userInfo.value?.bind_phone) {
      await handleBindSuccess('手机号绑定成功')
      return
    }

    message.error('绑定状态刷新失败，请稍后重试')
  } catch (error) {
    message.error(error instanceof Error ? error.message : '绑定手机号失败')
  }
}

async function startWechatBind() {
  try {
    await wechatBinder.startBind(async () => {
      await handleBindSuccess('微信绑定成功')
    })
  } catch (error) {
    message.error(error instanceof Error ? error.message : '获取微信绑定二维码失败')
  }
}

async function handleRefreshWechatQrcode() {
  try {
    await wechatBinder.refresh(async () => {
      await handleBindSuccess('微信绑定成功')
    })
  } catch (error) {
    message.error(error instanceof Error ? error.message : '刷新二维码失败')
  }
}

watch(visible, async (value) => {
  modalVisible.value = value

  if (!value) {
    resetState()
    return
  }

  await refreshBindUserInfo()

  if (bindType.value === 'phone' && userInfo.value?.bind_phone) {
    message.info('当前账号已绑定手机号')
    handleClose()
    return
  }

  if (bindType.value === 'wechat' && userInfo.value?.openid) {
    message.info('当前账号已绑定微信')
    handleClose()
    return
  }

  if (bindType.value === 'wechat') {
    await startWechatBind()
  }
})

watch(modalVisible, (value) => {
  if (!value && visible.value) {
    handleClose()
  }
})
</script>
